import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react"
import { useSetRefCallbacks, RefCallbacks } from "../App/useRefCallbacks"
import { ClassName, classNames } from "../Components"
import { Icon, Icons } from "../Components/Icon"

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
export type OnSelectCallback = (value?: any) => void;
export type MenuItemRender = () => ReactNode;
type SetMenuSelectedCallback = (selected: boolean) => void;

export interface MenuCallbacks {
  setSelected: (value: boolean) => void;
}

export enum MenuItemToggleState {
  disabled = 1,
  on = 2,
  off = 3,
}

export interface MenuItemProps {
  children?: ReactNode;
  className?: ClassName;
  disabled?: boolean;
  divider?: boolean;
  icon?: string;
  iconClass?: string;
  id: string;
  nonInteractive?: boolean;
  onClick?: OnSelectCallback;
  render?: MenuItemRender;
  setMenuSelected?: SetMenuSelectedCallback;
  title?: string | ReactNode;
  toggle?: MenuItemToggleState;
}

interface MenuProps {
  button: string | ReactNode;
  buttonClass?: string;
  callbacks?: RefCallbacks<MenuCallbacks>;
  children?: ReactNode;
  disabled?: boolean;
  global?: boolean;
  initOpen?: boolean;
  items?: MenuItemProps[];
  onClick?: OnSelectCallback;
}

export const MenuItemDividerProps: MenuItemProps = {
  divider: true,
  id: "divider",
};

export const MenuItemBlank: MenuItemProps = {
  id: "",
};

// Close callback published by an enclosing menu surface that is not itself a `Menu` --
// today the mobile hamburger panel in `Views/App/HeaderView`, which hosts the real
// MenuShip/MenuAccount/MenuHelp menus. A nested `Menu` closes that surface too whenever
// one of its own items closes the nested menu, so activating an item never leaves the
// panel sitting on top of the screen it just navigated to. Without it, tapping e.g.
// "Account Settings" on a phone that is already showing Account Settings changed nothing
// visible at all -- the App Store 2.1(a) "unresponsive" rejection (2026-07-31).
export const MenuCloseContext = createContext<(() => void) | undefined>(undefined);

export const MenuData = {
  menuLeftOffset(menuRight: number, viewportWidth: number, rightMargin: number = 30): string {
    const limit = viewportWidth - rightMargin;
    if (menuRight > limit) {
      const overflow = menuRight - limit;
      return `${-overflow}px`;
    }
    return "0";
  },
  visibleItems(items?: MenuItemProps[]): MenuItemProps[] {
    return (items || []).filter((item) => item.id != "");
  },
  isToggleOn(toggle?: MenuItemToggleState): boolean {
    return toggle == MenuItemToggleState.on;
  },
  isToggleDisabled(toggle?: MenuItemToggleState): boolean {
    return toggle == MenuItemToggleState.disabled;
  },
};

export function Menu({ button, buttonClass, callbacks, children, disabled, global, initOpen, items, onClick }: MenuProps) {
  const [selected, setSelected] = useState<boolean>(initOpen ?? false);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const closeEnclosingMenu = useContext(MenuCloseContext);

  // Only item activation propagates outwards: dismissing this menu through its own
  // overlay (tapping outside it) just backs out of the submenu, so the enclosing
  // surface has to stay open for another entry to be picked.
  const setItemMenuSelected = useCallback((value: boolean) => {
    setSelected(value);
    if (!value) {
      closeEnclosingMenu?.();
    }
  }, [closeEnclosingMenu]);

  useSetRefCallbacks(() => {
    return { setSelected: (value: boolean) => {
      setSelected(value);
    } };
  }, callbacks);

  useEffect(() => {
    const updateMenuPosition = () => {
      if (selected && menuItemsRef.current) {
        // Measure the unshifted CSS position, not the previous resize's correction.
        menuItemsRef.current.style.left = "";
        const menuRect = menuItemsRef.current.getBoundingClientRect();
        menuItemsRef.current.style.left = MenuData.menuLeftOffset(menuRect.right, window.innerWidth);
      }
    };

    updateMenuPosition();

    // Listen to window resize and orientation changes
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("orientationchange", updateMenuPosition);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("orientationchange", updateMenuPosition);
    };
  }, [selected]);

  return (
    <div className="Menu">
      {selected && global && <div className="Menu-overlay" onClick={() => setSelected(false)}></div>}
      <div className="Menu-items-container">
        {selected && (
          <div className="Menu-items" ref={menuItemsRef}>
            {children ? children : MenuData.visibleItems(items).map((item, index) => <MenuItem key={`item-${item.id}-${index}`} {...item} setMenuSelected={item.setMenuSelected || setItemMenuSelected} />)}
          </div>
        )}
      </div>
      <button className={classNames(["Menu-button", selected && "selected", buttonClass])} disabled={disabled || false} onClick={(e) => {
        e.preventDefault();
        if (disabled) {
          return;
        }
        if (onClick) {
          onClick(!selected);
        }
        setSelected(!selected);
      }}
      >{button}</button>
    </div>
  );
}

export function MenuItem({ children, className, disabled, divider, icon, iconClass, nonInteractive, onClick, render, setMenuSelected, title, toggle }: MenuItemProps) {
  if (divider) {
    return <MenuItemDivider />;
  }
  return (
    <div className={classNames(["Menu-item", disabled && "disabled", nonInteractive && "non-interactive", className])} onClick={() => {
      if (disabled || nonInteractive) {
        return;
      }
      if (onClick && onClick() && setMenuSelected) {
        setMenuSelected(false);
      }
    }}
    >
      {icon && <Icon className={iconClass} icon={icon} />}
      {title}
      {render && render()}
      {children}
      {toggle && (
        MenuData.isToggleOn(toggle) ? (
          <span className="button-style">
            <Icon icon={Icons.On1} className="toggle-button-left" />
            <Icon icon={Icons.On2} className="toggle-button-right" />
          </span>
        ) : (
          <span className={classNames(["button-style", MenuData.isToggleDisabled(toggle) && "disabled"])}>
            <Icon icon={Icons.Off1} className="toggle-button-left" />
            <Icon icon={Icons.Off2} className="toggle-button-right" />
          </span>
        ))}
    </div>
  );
}

export function MenuItemDivider() {
  return <hr className="Menu-item-divider" />;
}
